"use client"

import React, { useState, useContext, useEffect, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext"
import { useTenantPath } from "../../hooks/useTenantNavigation"

// Material UI
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  alpha,
  Button,
  useMediaQuery,
} from "@mui/material"

// Icons
import HomeIcon from "@mui/icons-material/Home"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits"
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import ViewListIcon from "@mui/icons-material/ViewList"
import PermDataSettingIcon from "@mui/icons-material/PermDataSetting"
import SettingsIcon from "@mui/icons-material/Settings"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts"
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt"
import TuneIcon from "@mui/icons-material/Tune"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"
import LocalAtmIcon from "@mui/icons-material/LocalAtm"
import RequestQuoteIcon from "@mui/icons-material/RequestQuote"
import CardTravelIcon from "@mui/icons-material/CardTravel"
import PaymentsIcon from "@mui/icons-material/Payments"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import LogoutIcon from "@mui/icons-material/Logout"
import InventoryIcon from "@mui/icons-material/Inventory"
import BallotIcon from '@mui/icons-material/Ballot';
import PersonIcon from "@mui/icons-material/Person"
import MenuRoundedIcon from "@mui/icons-material/MenuRounded"
import CloseIcon from "@mui/icons-material/Close"
import HailIcon from '@mui/icons-material/Hail';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// Imágenes
import imgLutenteNav from "../../assets/images/LutenteNavTransparent.png"
import lutenteMini from "../../assets/images/lutenteMini.png"

const drawerWidth = 280

const tieneAcceso = (id, accesos) => {
  return accesos?.some((a) => a.idMenuAcceso === id && a.eliminado === false)
}

const NavBar = () => {
  const theme = useTheme()
  const isTabletDown = useMediaQuery(theme.breakpoints.down("lg"))
  const location = useLocation()
  const { user, logout, setIsNavbarCollapsed, isNavbarCollapsed, getMenuAcceso, getParametrosGlobales } = useContext(ErpContext)
  const buildPath = useTenantPath() // 🌐 Hook para construir rutas con tenant

  // Estado para el logo del navbar
  const [logoNavbar, setLogoNavbar] = useState(null)

  // Estado para Drawer móvil
  const [mobileOpen, setMobileOpen] = useState(false)

  // Estado para los menús expandidos
  const [openMenus, setOpenMenus] = useState({
    VENTAS: false,
    INVENTARIO: false,
    TRANSACCION: false,
    ADMINISTRACION: false,
  })

  // Estado para el elemento activo
  const [activeItem, setActiveItem] = useState("")

  // Estado para las descripciones de menuAcceso
  const [menuDescripciones, setMenuDescripciones] = useState({})

  // Estado para la posición del menú flotante
  const [menuPosition, setMenuPosition] = useState({})

  // Efecto para cargar el logo del navbar desde parámetros globales
  useEffect(() => {
    const cargarLogoNavbar = async () => {
      try {
        if (getParametrosGlobales) {
          const parametros = await getParametrosGlobales(["logoNavbarTenant"])
          if (parametros && parametros.logoNavbarTenant) {
            setLogoNavbar(parametros.logoNavbarTenant)
          }
        }
      } catch (error) {
        console.error("Error al cargar logo del navbar:", error)
      }
    }

    cargarLogoNavbar()
  }, [getParametrosGlobales])

  // Efecto para cargar las descripciones de menuAcceso
  useEffect(() => {
    const cargarMenuDescripciones = async () => {
      try {
        const menusData = await getMenuAcceso()
// console.log(menusData)
        if (menusData) {
          // Crear un objeto con id como clave y descripcion como valor
          const descripciones = {}
          menusData.forEach(menu => {
            descripciones[menu.id] = menu.descripcion
          })
          setMenuDescripciones(descripciones)
        }
      } catch (error) {
        console.error("Error al cargar descripciones de menú:", error)
      }
    }

    cargarMenuDescripciones()
  }, [])

  // Efecto para cerrar menús cuando se hace clic fuera del navbar colapsado
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Solo aplicar en modo colapsado (desktop) y cuando hay menús abiertos
      if (isNavbarCollapsed && !isTabletDown && Object.values(openMenus).some(open => open)) {
        // Verificar si el clic fue dentro del navbar o dropdown
        const navbarElement = document.querySelector('[role="navigation"]')
        const dropdownElements = document.querySelectorAll('[data-dropdown="true"]')
        
        let clickedInsideNavbarOrDropdown = false
        
        // Verificar si el clic fue dentro del navbar
        if (navbarElement && navbarElement.contains(event.target)) {
          clickedInsideNavbarOrDropdown = true
        }
        
        // Verificar si el clic fue dentro de algún dropdown flotante
        dropdownElements.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInsideNavbarOrDropdown = true
          }
        })
        
        // Si el clic fue fuera del navbar y fuera de los dropdowns, cerrar todos los menús
        if (!clickedInsideNavbarOrDropdown) {
          setOpenMenus({
            VENTAS: false,
            INVENTARIO: false,
            TRANSACCION: false,
            ADMINISTRACION: false,
          })
        }
      }
    }

    // Agregar event listener
    document.addEventListener('mousedown', handleClickOutside)
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isNavbarCollapsed, isTabletDown, openMenus])

  const handleOpenMenuClick = () => {
    if (isTabletDown) {
      setMobileOpen(true)
    } else {
      setIsNavbarCollapsed(false)
    }
  }

  // Efecto para determinar el elemento activo basado en la ruta actual. esto es solo visual. *****
  useEffect(() => {
    const path = location.pathname

    // Mapeo de rutas a elementos del menú
    const routeToMenuItem = {
      "/": "DASHBOARD",
      "/gestionGenerarVenta": "VENTAS1",
      "/tablaVentas": "VENTAS2",
      "/gestionGenerarPresupuesto": "VENTAS3",
      "/tablaPresupuesto": "VENTAS4",
      "/INVENTARIO4": "INVENTARIO4",
      "/itemProveedor": "itemProveedor",
      "/COMPRAS5": "COMPRAS5",
      "/tablaListaCosto": "COMPRAS5",
      "/tablaListaPrecio": "VENTAS8",
      "/INVENTARIO1": "INVENTARIO1",
      "/verServicios": "COMPRAS8",
      "/crearCompra": "COMPRAS1",
      "/tablaCompra": "COMPRAS2",
      "/gestionMoneda": "ADMINISTRACION1",
      "/gestionMedioPago": "TRANSACCIONES1",
      "/gestionTipoTransaccion": "ADMINISTRACION10",
      "/Impuestos": "ADMINISTRACION2",
      "/gestionCategoria": "ADMINISTRACION3",
      "/gestionSubCategoria": "ADMINISTRACION4",
      "/tablaTransaccionComponent": "TRANSACCIONES5",
      "/tablaCuentaCorriente": "TRANSACCIONES6",
      "/gestionUbicaciones": "ADMINISTRACION5",
      "/gestionEntidad": "ADMINISTRACION6",
      "/gestionUsuarios": "ADMINISTRACION7",
      "/TablaEntidadProveedor": "COMPRAS4",
      "/gestionProveedoresComponent": "COMPRAS4",
      "/tablaVentasAfip": "TRANSACCIONES7",
      "/tablaVentasRetenciones": "TRANSACCIONES11",
      "/cajaComponentAnaliticos": "TRANSACCIONES8",
      "/cajaComponent": "TRANSACCIONES10",
      "/recetaComponent": "INVENTARIO2",
      "/tablaReceta": "INVENTARIO5",
      "/verBalance": "INVENTARIO8",
      "/gestionGastosComponent": "TRANSACCIONES4",
      "/gestionarAccesos": "ADMINISTRACION8",
      "/tablaGastosComponent": "COMPRAS7",
      "/tablaVentasEstados": "VENTAS9",
      "/verDestinos": "INVENTARIO6",
      "/gestionCliente": "CLIENTES2",
      "/clientePaqueteDetalle": "CLIENTES3",
    }

    // Mapeo de elementos del menú a sus secciones padre
    const menuItemToSection = {
      VENTAS1: "VENTAS",
      VENTAS2: "VENTAS",
      VENTAS3: "VENTAS",
      VENTAS4: "VENTAS",
      VENTAS5: "VENTAS",
      VENTAS6: "VENTAS",
      VENTAS8: "VENTAS",
      VENTAS9: "VENTAS",
      VENTAS7: "VENTAS",
      INVENTARIO4: "ADMINISTRACION",
      INVENTARIO1: "INVENTARIO",
      itemProveedor: "INVENTARIO",
      INVENTARIO2: "INVENTARIO",
      INVENTARIO5: "INVENTARIO",
      INVENTARIO6: "INVENTARIO",
      INVENTARIO8: "TRANSACCION",
      COMPRAS1: "TRANSACCION",
      COMPRAS2: "TRANSACCION",
      COMPRAS3: "TRANSACCION",
      COMPRAS4: "INVENTARIO",
      COMPRAS5: "TRANSACCION",
      COMPRAS7: "TRANSACCION",
      COMPRAS8: "INVENTARIO",
      CLIENTES2: "INVENTARIO",
      CLIENTES3: "INVENTARIO",
      TRANSACCIONES5: "TRANSACCION",
      TRANSACCIONES6: "TRANSACCION",
      TRANSACCIONES7: "TRANSACCION",
      TRANSACCIONES11: "TRANSACCION",
      TRANSACCIONES8: "TRANSACCION",
      TRANSACCIONES9: "TRANSACCION",
      TRANSACCIONES10: "TRANSACCION",
      ADMINISTRACION10: "ADMINISTRACION",
      ADMINISTRACION1: "ADMINISTRACION",
      ADMINISTRACION2: "ADMINISTRACION",
      ADMINISTRACION3: "ADMINISTRACION",
      ADMINISTRACION4: "ADMINISTRACION",
      ADMINISTRACION5: "ADMINISTRACION",
      ADMINISTRACION6: "ADMINISTRACION",
      ADMINISTRACION7: "ADMINISTRACION",
      ADMINISTRACION8: "ADMINISTRACION",
    }

    const menuItem = routeToMenuItem[path]
    if (menuItem) {
      setActiveItem(menuItem)
      const parentSection = menuItemToSection[menuItem]
      if (parentSection) {
        setOpenMenus((prev) => ({
          ...prev,
          [parentSection]: true,
        }))
      }
    }
  }, [location.pathname])

  // Función para alternar colapso en desktop
  const toggleNavbar = () => {
    // Si estamos colapsando la barra, cerramos todos los menús
    if (!isNavbarCollapsed) {
      setOpenMenus({
        VENTAS: false,
        INVENTARIO: false,
        TRANSACCION: false,
        ADMINISTRACION: false,
      })
    }
    setIsNavbarCollapsed(!isNavbarCollapsed)
  }

  // Toggle de submenús
  const handleToggleMenu = (menu, event) => {
    // Si el navbar está colapsado, capturar la posición del botón
    if (isNavbarCollapsed && !isTabletDown && event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect()
      setMenuPosition(prev => ({
        ...prev,
        [menu]: {
          top: rect.top,
          left: 72, // Ancho del navbar colapsado
        }
      }))
    }
    
    if (isNavbarCollapsed) {
      const newOpen = {
        VENTAS: false,
        INVENTARIO: false,
        TRANSACCION: false,
        ADMINISTRACION: false,
      }
      newOpen[menu] = !openMenus[menu]
      setOpenMenus(newOpen)
    } else {
      setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }))
    }
  }

  // Clic en ítem
  const handleMenuItemClick = (item) => {
    setActiveItem(item)
    // En móvil, cerrar el Drawer tras navegar
    if (isTabletDown) {
      setMobileOpen(false)
    }
  }

  // Función helper para obtener la descripción del menú desde la BD o usar el label por defecto
  const getMenuLabel = (id, defaultLabel) => {
    return menuDescripciones[id] || defaultLabel
  }

  // Estructura del menú generada dinámicamente usando las descripciones de la BD
  const menuStructure = useMemo(() => [
    {
      id: "DASHBOARD",
      label: getMenuLabel("DASHBOARD", "Inicio"),
      icon: <HomeIcon />,
      path: "",
      allowedRoles: ["Administrador", "Vendedor"],
      children: [],
    },
    {
      id: "VENTAS",
      label: getMenuLabel("VENTAS", "Ventas"),
      icon: <MonetizationOnIcon />,
      children: [
        {
          id: "VENTAS1",
          label: getMenuLabel("VENTAS1", "Crear Venta"),
          icon: <ShoppingCartIcon />,
          path: "gestionGenerarVenta",
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS2",
          label: getMenuLabel("VENTAS2", "Ventas"),
          icon: <ProductionQuantityLimitsIcon />,
          path: "tablaVentas",
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS3",
          label: getMenuLabel("VENTAS3", "Crear Presupuesto"),
          icon: <BallotIcon />,
          path: "gestionGenerarPresupuesto",
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS4",
          label: getMenuLabel("VENTAS4", "Presupuestos"),
          icon: <ProductionQuantityLimitsIcon />,
          path: "tablaPresupuesto",
          allowedRoles: ["Administrador"],
        },
        /*{
          id: "VENTAS5",
          label: getMenuLabel("VENTAS5", "Ventas Detalle"),
          icon: <ProductionQuantityLimitsIcon />,
          path: "/verVentasDetalle",
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS6",
          label: getMenuLabel("VENTAS6", "Presupuesto Detalle"),
          icon: <ProductionQuantityLimitsIcon />,
          path: "/verPresupuestoDetalle",
          allowedRoles: ["Administrador"],
        },*/
        {
          id: "VENTAS8",
          label: getMenuLabel("VENTAS8", "Lista de Precios"),
          icon: <ViewListIcon />,
          path: "tablaListaPrecio",
          allowedRoles: ["Administrador"],
        },
        /*{
          id: "VENTAS9",
          label: getMenuLabel("VENTAS9", "Pedidos"),
          icon: <ViewListIcon />,
          path: "/tablaVentasEstados",
          allowedRoles: ["Administrador"],
        },*/
        
        
      ],
    },
    {
      id: "INVENTARIO",
      label: getMenuLabel("INVENTARIO", "Viajes"),
      icon: <DirectionsBusIcon />,
      children: [
        {
          id: "INVENTARIO6",
          label: getMenuLabel("INVENTARIO6", "Destinos"),
          icon: <HailIcon />,
          path: "verDestinos",
          allowedRoles: ["Administrador"],
        },
        {
          id: "INVENTARIO5",
          label: getMenuLabel("INVENTARIO5", "Grupos de Viaje"),
          icon: <ViewListIcon />,
          path: "tablaReceta",
          allowedRoles: ["Administrador"],
        },
        {
          id: "CLIENTES3",
          label: getMenuLabel("CLIENTES3", "Clientes"),
          icon: <PersonPinIcon />,
          path: "clientePaqueteDetalle",
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS4",
          label: getMenuLabel("COMPRAS4", "Proveedores"),
          icon: <LocalShippingIcon />,
          path: "gestionProveedoresComponent",
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS8",
          label: getMenuLabel("COMPRAS8", "Servicios"),
          icon: <InventoryIcon />,
          path: "verServicios",
          allowedRoles: ["Administrador", "Vendedor"],
        },
      ],
    },
    {
      id: "TRANSACCION",
      label: getMenuLabel("TRANSACCION", "Administración"),
      icon: <ManageAccountsIcon />,
      children: [
        {
          id: "INVENTARIO8",
          label: getMenuLabel("INVENTARIO8", "Balance"),
          icon: <AccountBalanceWalletIcon />,
          path: "verBalance",
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES10",
          label: getMenuLabel("TRANSACCIONES10", "Caja"),
          icon: <PaymentsIcon />,
          path: "cajaComponent",
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS7",
          label: getMenuLabel("COMPRAS7", "Gastos"),
          icon: <PaymentsIcon />,
          path: "tablaGastosComponent",
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES5",
          label: getMenuLabel("TRANSACCIONES5", "Transacciones"),
          icon: <PaymentsIcon />,
          path: "tablaTransaccionComponent",
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES7",
          label: getMenuLabel("TRANSACCIONES7", "ARCA"),
          icon: <PaymentsIcon />,
          path: "tablaVentasAfip",
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES11",
          label: getMenuLabel("TRANSACCIONES11", "Retenciones ARCA"),
          icon: <PaymentsIcon />,
          path: "tablaVentasRetenciones",
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "ADMINISTRACION",
      label: getMenuLabel("ADMINISTRACION", "Configuración"),
      icon: <SettingsIcon />,
      children: [
        {
          id: "ADMINISTRACION5",
          label: getMenuLabel("ADMINISTRACION5", "General"),
          icon: <TuneIcon />,
          path: "gestionUbicaciones",
          allowedRoles: ["Administrador"],
        },
        // {
        //   id: "ADMINISTRACION1",
        //   label: getMenuLabel("ADMINISTRACION1", "Monedas"),
        //   icon: <LocalAtmIcon />,
        //   path: "gestionMoneda",
        //   allowedRoles: ["Administrador"],
        // },
        {
          id: "ADMINISTRACION2",
          label: getMenuLabel("ADMINISTRACION2", "Impuestos"),
          icon: <PaymentsIcon />,
          path: "Impuestos",
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION3",
          label: getMenuLabel("ADMINISTRACION3", "Categoria Gastos"),
          icon: <CardTravelIcon />,
          path: "gestionCategoria",
          allowedRoles: ["Administrador"],
        },
        // {
        //   id: "ADMINISTRACION4",
        //   label: getMenuLabel("ADMINISTRACION4", "SubCategoria Transaccion"),
        //   icon: <CardTravelIcon />,
        //   path: "/gestionSubCategoria",
        //   allowedRoles: ["Administrador"],
        // },
        {
          id: "ADMINISTRACION6",
          label: getMenuLabel("ADMINISTRACION6", "Entidad"),
          icon: <PeopleAltIcon />,
          path: "gestionEntidad",
          allowedRoles: ["Administrador"],
        },
        // {
        //   id: "ADMINISTRACION7",
        //   label: getMenuLabel("ADMINISTRACION7", "Usuarios"),
        //   icon: <PeopleAltIcon />,
        //   path: "gestionUsuarios",
        //   allowedRoles: ["Administrador"],
        // },
        {
          id: "ADMINISTRACION8",
          label: getMenuLabel("ADMINISTRACION8", "Gestión de usuarios"),
          icon: <PeopleAltIcon />,
          path: "gestionarAccesos",
          allowedRoles: ["Administrador"],
        },
        {
          id: "INVENTARIO4",
          label: getMenuLabel("INVENTARIO4", "Atributos viajes"),
          icon: <ViewListIcon />,
          path: "Atributos",
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION9",
          label: getMenuLabel("ADMINISTRACION9", "Medios de pago"),
          icon: <PaymentsIcon />,
          path: "gestionMedioPago",
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION10",
          label: getMenuLabel("ADMINISTRACION10", "Tipos de transaccion"),
          icon: <RequestQuoteIcon />,
          path: "gestionTipoTransaccion",
          allowedRoles: ["Administrador"],
        },
      ],
    },
  ], [menuDescripciones])

  const userAccesses = user?.accesos || []

  // Filtrado: mostrar solo menús y submenús con acceso
  const filteredMenu = menuStructure
    .map((menu) => {
      const filteredChildren =
        menu.children && menu.children.length > 0
          ? menu.children.filter((child) => tieneAcceso(child.id, userAccesses))
          : []
      return { ...menu, children: filteredChildren }
    })
    .filter((menu) => {
      if (menu.children.length === 0) {
        return tieneAcceso(menu.id, userAccesses)
      }
      const tieneAccesoMenuPrincipal = tieneAcceso(menu.id, userAccesses)
      const tieneHijosConAcceso = menu.children.length > 0
      // INVENTARIO (Viajes): mostrar si tiene acceso al padre O a algún hijo (ej. CLIENTES2, COMPRAS4)
      if (menu.id === "INVENTARIO") {
        return tieneHijosConAcceso && (tieneAccesoMenuPrincipal || tieneHijosConAcceso)
      }
      return tieneAccesoMenuPrincipal && tieneHijosConAcceso
    })

  // Contenido común del Drawer (desktop y móvil)
  const DrawerContent = ({ isMobileMode = false }) => (
    <Box
      role="navigation"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo y controles */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "space-between",
          padding: isNavbarCollapsed && !isMobileMode ? 1 : 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          width: "100%",
        }}
      >
        {isNavbarCollapsed && !isMobileMode ? (
          <img
            src={logoNavbar || "/placeholder.svg"}
            alt="Small Logo"
            style={{ height: 40, cursor: "pointer" }}
            onClick={toggleNavbar}
          />
        ) : (
          <img
            src={logoNavbar || imgLutenteNav || "/placeholder.svg"}
            alt="Logo"
            style={{ height: 40, cursor: "pointer" }}
            onClick={isMobileMode ? () => setMobileOpen(false) : toggleNavbar}
          />
        )}

        {/* Botón de colapso en desktop y botón de cierre en móvil */}
        {!isMobileMode ? (
          !isNavbarCollapsed && (
            <IconButton onClick={toggleNavbar} size="small" aria-label="Colapsar menú">
              <ChevronLeftIcon />
            </IconButton>
          )
        ) : (
          <IconButton onClick={() => setMobileOpen(false)} size="small" aria-label="Cerrar menú">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Menú de navegación */}
      <List
        sx={{
          padding: 1,
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.2),
            borderRadius: "4px",
          },
        }}
      >
        {filteredMenu.map((menu) => (
          <React.Fragment key={menu.id}>
            {menu.children.length === 0 ? (
              // Elemento sin hijos
              <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={buildPath(menu.path)}
                  onClick={() => handleMenuItemClick(menu.id)}
                  selected={activeItem === menu.id}
                  sx={{
                    minHeight: 48,
                    justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "initial",
                    px: 2.5,
                    borderRadius: "8px",
                    backgroundColor: activeItem === menu.id ? "rgb(244, 177, 14)" : "transparent",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                  }}
                >
                  <Tooltip title={isNavbarCollapsed && !isMobileMode ? menu.label : ""} placement="right">
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isNavbarCollapsed && !isMobileMode ? 0 : 2,
                        justifyContent: "center",
                        color: activeItem === menu.id ? "rgb(244, 177, 14)" : "inherit",
                      }}
                    >
                      {menu.icon}
                    </ListItemIcon>
                  </Tooltip>
                  {(!isNavbarCollapsed || isMobileMode) && (
                    <ListItemText
                      primary={menu.label}
                      primaryTypographyProps={{
                        fontWeight: activeItem === menu.id ? 600 : 400,
                        color: activeItem === menu.id ? "rgb(244, 177, 14)" : "inherit",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ) : (
              // Elemento con submenú
              <>
                <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                  <ListItemButton
                    onClick={(event) => handleToggleMenu(menu.id, event)}
                    sx={{
                      minHeight: 48,
                      justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "initial",
                      px: 2.5,
                      borderRadius: "8px",
                      backgroundColor: openMenus[menu.id] ? alpha(theme.palette.primary.main, 0.05) : "transparent",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <Tooltip title={isNavbarCollapsed && !isMobileMode ? menu.label : ""} placement="right">
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isNavbarCollapsed && !isMobileMode ? 0 : 2,
                          justifyContent: "center",
                          color: openMenus[menu.id] ? "rgb(244, 177, 14)" : "inherit",
                        }}
                      >
                        <Badge
                          color="default"
                          variant="dot"
                          invisible={!menu.children.some((child) => activeItem === child.id)}
                        >
                          {menu.icon}
                        </Badge>
                      </ListItemIcon>
                    </Tooltip>
                    {(!isNavbarCollapsed || isMobileMode) && (
                      <>
                        <ListItemText
                          primary={menu.label}
                          primaryTypographyProps={{
                            fontWeight: openMenus[menu.id] ? 600 : 400,
                            color: openMenus[menu.id] ? "rgb(244, 177, 14)" : "inherit",
                          }}
                        />
                        {openMenus[menu.id] ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Submenú en desktop expandido */}
                {!isNavbarCollapsed && !isMobileMode && (
                  <Collapse in={openMenus[menu.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          component={Link}
                          to={buildPath(child.path)}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={activeItem === child.id}
                          sx={{
                            pl: 4,
                            py: 0.75,
                            minHeight: 40,
                            borderRadius: "8px",
                            ml: 2,
                            mr: 1,
                            mb: 0.5,
                            backgroundColor:
                              activeItem === child.id ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight: activeItem === child.id ? 600 : 400,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}

                {/* Menú flotante para modo colapsado (solo desktop) */}
                {!isMobileMode && isNavbarCollapsed && openMenus[menu.id] && (
                  <Box
                    data-dropdown="true"
                    sx={{
                      position: "fixed",
                      left: menuPosition[menu.id]?.left || 72,
                      top: menuPosition[menu.id]?.top || 8,
                      zIndex: 1300,
                      bgcolor: "background.paper",
                      boxShadow: 4,
                      borderRadius: 1,
                      width: 220,
                      maxHeight: "calc(100vh - 16px)",
                      overflowY: "auto",
                      // Asegurar que no se salga por abajo
                      transform: menuPosition[menu.id]?.top && 
                        (menuPosition[menu.id].top + 400 > window.innerHeight) 
                        ? `translateY(calc(-100% + 48px))` 
                        : undefined,
                    }}
                  >
                    <List>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          component={Link}
                          to={buildPath(child.path)}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={activeItem === child.id}
                          sx={{
                            py: 1,
                            "&.Mui-selected": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Submenú en móvil: mostrar dentro del Drawer temporal cuando está abierto */}
                {isMobileMode && (
                  <Collapse in={openMenus[menu.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={child.id}
                          component={Link}
                          to={buildPath(child.path)}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={activeItem === child.id}
                          sx={{
                            pl: 4,
                            py: 1,
                            minHeight: 44,
                            borderRadius: "8px",
                            ml: 1,
                            mr: 1,
                            mb: 0.5,
                            backgroundColor:
                              activeItem === child.id ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: "0.95rem" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Sección de usuario */}
      <Box
        sx={{
          p: isNavbarCollapsed && !isMobileMode ? 1 : 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          display: "flex",
          flexDirection: isNavbarCollapsed && !isMobileMode ? "column" : "row",
          alignItems: "center",
          justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "space-between",
        }}
      >
        {user ? (
          <>
            {(!isNavbarCollapsed || isMobileMode) && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: theme.palette.primary.main,
                    fontSize: "1rem",
                  }}
                >
                  {user.nombreUsuario?.charAt(0).toUpperCase() || "U"}
                </Avatar>
                <Box sx={{ ml: 1.5, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                    {user.nombreUsuario}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.rolUsuario || "Usuario"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Tooltip title="Cerrar sesión">
              <IconButton
                onClick={logout}
                size="small"
                sx={{
                  ml: isNavbarCollapsed && !isMobileMode ? 0 : 1,
                  mt: isNavbarCollapsed && !isMobileMode ? 1 : 0,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
                }}
                aria-label="Cerrar sesión"
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Button
            component={Link}
            to={buildPath("login")}
            variant="contained"
            fullWidth
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {isNavbarCollapsed && !isMobileMode ? <PersonIcon /> : "Iniciar sesión"}
          </Button>
        )}
      </Box>
    </Box>
  )

  // Render: en desktop, Drawer permanente; en móvil, Drawer temporal + FAB
  return (
    <>
      {isTabletDown ? (
        <>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ "aria-label": "Menú de navegación" }}
            sx={{
              zIndex: theme.zIndex.drawer + 2,
              "& .MuiDrawer-paper": {
                width: "min(86vw, 360px)",
                boxSizing: "border-box",
                backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e1e1e",
              },
            }}
          >
            <DrawerContent isMobileMode />
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          open={!isNavbarCollapsed}
          sx={{
            width: isNavbarCollapsed ? 72 : drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isNavbarCollapsed ? 72 : drawerWidth,
              boxSizing: "border-box",
              backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e1e1e",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              transition: theme.transitions.create(["width"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
            },
          }}
        >
          <DrawerContent />
        </Drawer>
      )}

      {/* Botón hamburger superior derecho (sutil y pequeño) */}
      {isTabletDown && !mobileOpen && (
        <Tooltip title="Menú">
          <IconButton
            onClick={handleOpenMenuClick}
            size="small"
            aria-label="Abrir menú"
            sx={{
              position: "fixed",
              top: "calc(12px + env(safe-area-inset-top))",
              right: "calc(12px + env(safe-area-inset-right))",
              zIndex: theme.zIndex.drawer + 4,
              bgcolor: alpha(theme.palette.background.paper, 0.85),
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
              "&:hover": { bgcolor: alpha(theme.palette.background.paper, 1) },
              width: 36,
              height: 36,
            }}
          >
            <MenuRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  )
}

export default NavBar
